'use client'
import { useState } from 'react'
import { iranProvinces, IRAN_MAP_VIEWBOX } from '@/lib/data/iranMap'

type IranMapProps = {
  /** Controlled active province id. When omitted, the map manages its own hover state. */
  activeId?: string | null
  /** Fired on mouse enter/leave of a province (desktop hover). */
  onHover?: (id: string | null) => void
  /** Fired on click/tap of a province (touch-friendly selection). */
  onSelect?: (id: string) => void
}

export default function IranMap({ activeId, onHover, onSelect }: IranMapProps) {
  const [internalHovered, setInternalHovered] = useState<string | null>(null)
  const controlled = activeId !== undefined
  const active = controlled ? activeId : internalHovered

  const handleEnter = (id: string) => {
    if (!controlled) setInternalHovered(id)
    onHover?.(id)
  }
  const handleLeave = () => {
    if (!controlled) setInternalHovered(null)
    onHover?.(null)
  }

  return (
    <div className="relative w-full select-none">
      <style>{`
        @keyframes neon-pulse {
          0%,100% { filter: drop-shadow(0 0 3px #00b4d8) drop-shadow(0 0 8px #0335b7) drop-shadow(0 0 2px #00b4d8); opacity: 1; }
          25%      { filter: drop-shadow(0 0 6px #3b82f6) drop-shadow(0 0 14px #0335b7) drop-shadow(0 0 4px #00b4d8); opacity: 0.82; }
          50%      { filter: drop-shadow(0 0 10px #00b4d8) drop-shadow(0 0 20px #3b82f6) drop-shadow(0 0 6px #0335b7); opacity: 1; }
          75%      { filter: drop-shadow(0 0 5px #3b82f6) drop-shadow(0 0 12px #00b4d8) drop-shadow(0 0 3px #0335b7); opacity: 0.88; }
        }
        @keyframes neon-flicker {
          0%,100% { opacity: 1; }
          8%       { opacity: 0.85; }
          16%      { opacity: 1; }
          48%      { opacity: 0.92; }
          56%      { opacity: 1; }
          72%      { opacity: 0.8; }
          80%      { opacity: 1; }
        }
      `}</style>

      <svg
        viewBox={IRAN_MAP_VIEWBOX}
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-auto"
        style={{ maxHeight: 'none', transform: 'scaleY(-1)' }}
      >
        <defs>
          <filter id="neon-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="60" result="blur" />
            <feColorMatrix
              in="blur"
              type="matrix"
              values="0 0 0 0 0  0 0.7 0 0 0.85  0 0 1 0 0  0 0 0 0.6 0"
              result="coloredBlur"
            />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Province fills */}
        {iranProvinces.map((p) => (
          <path
            key={p.id + '-fill'}
            d={p.path}
            fill={active === p.id ? 'rgba(0,180,216,0.20)' : 'rgba(3,53,183,0.08)'}
            stroke="none"
            style={{ transition: 'fill 0.25s ease' }}
          />
        ))}

        {/* Province borders — base layer (always visible, dim) */}
        {iranProvinces.map((p) => (
          <path
            key={p.id + '-border-base'}
            d={p.path}
            fill="none"
            stroke={active === p.id ? 'transparent' : 'rgba(0,180,216,0.35)'}
            strokeWidth="6"
            pointerEvents="none"
            style={{ transition: 'stroke 0.2s ease' }}
          />
        ))}

        {/* Province borders — neon active layer */}
        {iranProvinces.map((p) =>
          active === p.id ? (
            <path
              key={p.id + '-border-neon'}
              d={p.path}
              fill="none"
              stroke="#00d4ff"
              strokeWidth="10"
              pointerEvents="none"
              style={{
                animation:
                  'neon-pulse 1.6s ease-in-out infinite, neon-flicker 3.2s ease-in-out infinite',
                filter:
                  'drop-shadow(0 0 6px #00b4d8) drop-shadow(0 0 16px #3b82f6) drop-shadow(0 0 40px #0335b7)',
              }}
            />
          ) : null,
        )}

        {/* Hit targets — capture hover, tap & keyboard. Focus mirrors hover so
            keyboard users get the same highlight; Enter/Space select. */}
        {iranProvinces.map((p) => (
          <path
            key={p.id + '-hit'}
            d={p.path}
            fill="transparent"
            stroke="none"
            role="button"
            tabIndex={0}
            aria-label={p.name}
            style={{ cursor: 'pointer' }}
            onMouseEnter={() => handleEnter(p.id)}
            onMouseLeave={handleLeave}
            onFocus={() => handleEnter(p.id)}
            onBlur={handleLeave}
            onClick={() => onSelect?.(p.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onSelect?.(p.id)
              }
            }}
          />
        ))}
      </svg>
    </div>
  )
}
